import { cn } from "@tellescope/design-system";

import { BlockProps } from "../../../types";
import { getTicketPriorityOptions, getTicketStageOptions, useTicketsBlock } from "../../../hooks";
import { BlockHeader } from "../BlockHeader";
import { BlockContent } from "../BlockContent";
import { TicketsFilterPopover } from "./TicketsFilterPopover";
import { TicketsListItem } from "./TicketsListItem";
import { useTranslation } from "react-i18next";
import { BlockPaginatedList } from "../BlockPaginatedList";
import { BlockEmptyState } from "../BlockEmptyState";

export const TicketsBlock = ({
  onMaximize,
  alwaysShowButtons = false,
  className,
  contentClassName,
  onOpenTicket,
  ...rest
}: BlockProps) => {
  const { t } = useTranslation("tickets");
  const {
    parsedInfo,
    tickets,
    isLoading,
    fetchNextPage,
    usersById,
    filterBlock,
    filterFieldOptions,
    onFilterChange,
    tagOptions,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    expanded,
    blockLayout,
    canEditTitle,
    canEditBlock,
    canCreateBlock,
    canDeleteBlock,
  } = useTicketsBlock({ ...rest, onMaximize, alwaysShowButtons, className });

  const stageOptions = getTicketStageOptions();
  const priorityOptions = getTicketPriorityOptions();

  return (
    <div className={cn("group", className)}>
      <BlockHeader
        editMode={editMode}
        setEditMode={setEditMode}
        isEditingTitle={isEditingTitle}
        setIsEditingTitle={setIsEditingTitle}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        canEditTitle={canEditTitle}
        parsedInfo={parsedInfo}
        updateInfo={updateInfo}
        duplicateBlock={duplicateBlock}
        deleteBlock={deleteBlock}
        canCreateBlock={canCreateBlock}
        canDeleteBlock={canDeleteBlock}
        onMaximize={onMaximize}
        alwaysShowButtons={alwaysShowButtons}
        actions={
          canEditBlock && (
            <TicketsFilterPopover
              filterBlock={filterBlock}
              fieldOptions={filterFieldOptions}
              stageOptions={stageOptions}
              priorityOptions={priorityOptions}
              tagOptions={tagOptions}
              onFilterChange={onFilterChange}
            />
          )
        }
      />

      <BlockContent
        editMode={editMode}
        setEditMode={setEditMode}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("flex flex-col", contentClassName)}
      >
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <BlockEmptyState>{t("Loading tickets...")}</BlockEmptyState>
          </div>
        ) : !tickets.length ? (
          <div className="flex flex-1 items-center justify-center">
            <BlockEmptyState>{t("No tickets found")}</BlockEmptyState>
          </div>
        ) : (
          <BlockPaginatedList
            className="flex flex-col flex-grow"
            scrollbar="floating"
            fadeWhenClipped
            onBottomReached={() => fetchNextPage()}
          >
            {tickets.map((ticket) => {
              const owner = ticket.owner ? usersById[ticket.owner] : undefined;
              return (
                <TicketsListItem
                  key={ticket.id}
                  title={ticket.title}
                  owner={owner}
                  className="mb-2"
                  onOpen={onOpenTicket?.(ticket.id)}
                />
              );
            })}
          </BlockPaginatedList>
        )}
      </BlockContent>
    </div>
  );
};
